import { Link as RouterLink } from 'react-router-dom'
import { useState } from 'react'
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  useColorMode,
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { EnterpriseContactModal } from './EnterpriseContactModal'
import { WaitlistModal } from './WaitlistModal'

const MotionBox = motion.create(Box)

const fadeInUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' as const } },
}

interface PlanFeature {
  text: string
  included: boolean
}

interface Plan {
  name: string
  price: string
  period: string
  description: string
  features: PlanFeature[]
  cta: string
  ctaLink: string
  highlighted?: boolean
}

const plans: Plan[] = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Perfect for trying out myrkup',
    features: [
      { text: '20 active shares', included: true },
      { text: '1 MB total storage', included: true },
      { text: '7-day max expiration', included: true },
      { text: 'MCP: 30 req/min, 1K req/day', included: true },
      { text: 'MCP + REST API access', included: true },
      { text: 'Burn after read', included: true },
      { text: 'Password-protected shares', included: true },
      { text: 'Editor/management UI', included: true },
      { text: 'No captcha', included: false },
      { text: 'Custom short URLs', included: false },
      { text: 'View analytics', included: false },
      { text: 'Custom branding/themes', included: false },
      { text: 'Edit published shares', included: false },
      { text: 'Embed access', included: false },
    ],
    cta: 'Get Started',
    ctaLink: '/editor',
    highlighted: true,
  },
  {
    name: 'Starter',
    price: '$3',
    period: '/month',
    description: 'For regular users who need more',
    features: [
      { text: '100 active shares', included: true },
      { text: '25 MB total storage', included: true },
      { text: '30-day max expiration', included: true },
      { text: 'MCP: 60 req/min, 5K req/day', included: true },
      { text: 'MCP + REST API access', included: true },
      { text: 'Burn after read', included: true },
      { text: 'Password-protected shares', included: true },
      { text: 'Editor/management UI', included: true },
      { text: 'No captcha', included: true },
      { text: 'Custom short URLs', included: false },
      { text: 'View analytics', included: false },
      { text: 'Custom branding/themes', included: false },
      { text: 'Edit published shares', included: false },
      { text: 'Embed access', included: false },
    ],
    cta: 'Join the waitlist',
    ctaLink: '/subscribe/starter',
  },
  {
    name: 'Pro',
    price: '$8',
    period: '/month',
    description: 'For power users with heavy usage',
    features: [
      { text: 'Unlimited active shares', included: true },
      { text: '100 MB total storage', included: true },
      { text: 'No expiration option', included: true },
      { text: 'MCP: 120 req/min, 15K req/day', included: true },
      { text: 'MCP + REST API access', included: true },
      { text: 'Burn after read', included: true },
      { text: 'Password-protected shares', included: true },
      { text: 'Editor/management UI', included: true },
      { text: 'No captcha', included: true },
      { text: 'Custom short URLs', included: true },
      { text: 'View analytics', included: true },
      { text: 'Custom branding/themes', included: true },
      { text: 'Edit published shares', included: true },
      { text: 'Embed access', included: true },
    ],
    cta: 'Join the waitlist',
    ctaLink: '/subscribe/pro',
  },
]

export function PricingSection() {
  const { colorMode } = useColorMode()
  const isDark = colorMode === 'dark'
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [waitlistPlan, setWaitlistPlan] = useState<string | null>(null)

  return (
    <Box
      id="pricing"
      scrollMarginTop="72px"
      py={{ base: 16, md: 24 }}
      bg={isDark ? 'transparent' : 'white'}
    >
      <Container maxW="1100px" px={{ base: 4, md: 8 }}>
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          variants={fadeInUp}
        >
          <Heading
            as="h2"
            fontFamily="heading"
            fontWeight={700}
            fontSize={{ base: '1.75rem', md: '2rem' }}
            letterSpacing="-0.025em"
            color={isDark ? 'stone.50' : 'stone.900'}
            textAlign="center"
            mb={3}
          >
            Pricing
          </Heading>
          <Text
            textAlign="center"
            fontSize="1.125rem"
            color="stone.400"
            mb={12}
          >
            Free to start. Upgrade when you need more.
          </Text>

          {/* Three cards */}
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mb={8}>
            {plans.map((plan) => (
              <Box
                key={plan.name}
                p={8}
                bg={isDark ? 'stone.900' : 'white'}
                borderRadius="12px"
                border="1px solid"
                borderColor={plan.highlighted ? 'brand.500' : (isDark ? 'stone.700' : 'stone.200')}
                display="flex"
                flexDirection="column"
              >
                <VStack align="stretch" spacing={5} flex={1}>
                  {/* Plan name + price */}
                  <Box>
                    <Text
                      fontSize="13px"
                      fontWeight={500}
                      color="stone.500"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                      mb={1}
                    >
                      {plan.name}
                    </Text>
                    <Flex align="baseline" gap={1}>
                      <Text
                        fontFamily="heading"
                        fontWeight={700}
                        fontSize="2rem"
                        color={isDark ? 'stone.50' : 'stone.900'}
                        letterSpacing="-0.02em"
                      >
                        {plan.price}
                      </Text>
                      {plan.period && (
                        <Text fontSize="14px" color="stone.500">
                          {plan.period}
                        </Text>
                      )}
                    </Flex>
                    <Text fontSize="14px" color="stone.400" mt={1}>
                      {plan.description}
                    </Text>
                  </Box>

                  {/* Features */}
                  <VStack align="stretch" spacing={2}>
                    {plan.features.map((feature) => (
                      <Flex key={feature.text} align="center" gap={2}>
                        <Text
                          color={feature.included ? 'green.500' : 'red.500'}
                          fontSize="14px"
                          flexShrink={0}
                        >
                          {feature.included ? '✓' : '×'}
                        </Text>
                        <Text
                          fontSize="14px"
                          color={feature.included
                            ? (isDark ? 'stone.300' : 'stone.700')
                            : (isDark ? 'stone.600' : 'stone.400')
                          }
                        >
                          {feature.text}
                        </Text>
                      </Flex>
                    ))}
                  </VStack>

                  {/* Spacer */}
                  <Box flex={1} />

                  {/* CTA */}
                  {plan.cta === 'Join the waitlist' ? (
                    <Button
                      w="full"
                      variant="ghost"
                      size="md"
                      onClick={() => setWaitlistPlan(plan.name)}
                    >
                      {plan.cta}
                    </Button>
                  ) : (
                    <Button
                      as={RouterLink}
                      to={plan.ctaLink}
                      w="full"
                      variant={plan.highlighted ? 'solid' : 'ghost'}
                      size="md"
                    >
                      {plan.cta}
                    </Button>
                  )}
                </VStack>
              </Box>
            ))}
          </SimpleGrid>

          {/* Enterprise contact */}
          <Box
            mt={12}
            p={8}
            bg={isDark ? 'stone.800' : 'stone.100'}
            borderRadius="12px"
            textAlign="center"
          >
            <Text
              fontSize="1.25rem"
              fontWeight={600}
              color={isDark ? 'stone.50' : 'stone.900'}
              mb={2}
            >
              Need more?
            </Text>
            <Text
              fontSize="1rem"
              color="stone.500"
              mb={4}
            >
              Custom limits, SSO, compliance, on-premise deployment, or something else.
            </Text>
            <Button
              variant="outline"
              size="md"
              onClick={() => setIsContactModalOpen(true)}
            >
              Contact us
            </Button>
          </Box>
        </MotionBox>
      </Container>

      <WaitlistModal
        isOpen={waitlistPlan !== null}
        onClose={() => setWaitlistPlan(null)}
        planName={waitlistPlan ?? ''}
      />

      <EnterpriseContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </Box>
  )
}
